import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Page, TransitionUrl, TranslationPath } from '../../../contexts/kioskConfiguration/type';
import { distanceBetweenCoordsInMeters, getGeolocation } from '../../../utils/gps';
import { useKioskConfiguration } from '../../../contexts/kioskConfiguration/KioskConfigurationContext';
import { TextListTemplate } from '../textList/textListTemplate';
import {
  askPermissionForPushNotification, getPushNotificationSubscription,
  isPushNotificationSupported,
  subscribeUserToPushNotification, unsubscribeUserFromPushNotification
} from '../../../utils/notification';
import { ToggleButton } from '../../../components/toggleButton/ToggleButton';
import { getTranslationByPath } from '../../../utils/translations';
import useLanguage from '../../../contexts/language/LanguageContext';
import { isIos } from '../../../utils/userAgent';
import { useDispatch, useSelector } from 'react-redux';
import { setTransportationForm } from '../../../store/slices/transportation/transportationSlice';
import { RootState } from '../../../store/store';


type Block = {
  image: string
  translation: TranslationPath
}

export type InfoForDriverTemplateType = {
  transitionUrl: TransitionUrl
  infoTransitionUrl: TransitionUrl
  blocks: Block[]
  locationErrorText: string
  outOfWarehouseText: string
  infoBtnText: string
  toggleNotificationsText: string
  iosNotificationText: string
}

type Props = {
  page: Page<InfoForDriverTemplateType>
}

export const InfoForDriverTemplate = (props: Props): React.ReactElement => {
  const kioskConfiguration = useKioskConfiguration()
  const language = useLanguage()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [distance, setDistance] = useState<number | null>(null)
  const [geolocationBypassed, setGeolocationBypassed] = useState<boolean>(false)
  const [hasLocationError, setHasLocationError] = useState<boolean>(false)
  const [isSubscribedToPushNotifications, setIsSubscribedToPushNotifications] = useState<boolean>(false)
  const transportationForm = useSelector((state: RootState) => state.transportation.transportationForm)

  const dispatch = useDispatch()

  useEffect(() => {
    (async () => {
      try {
        if (isPushNotificationSupported()) {
          setIsSubscribedToPushNotifications((await getPushNotificationSubscription()) !== null)
        } else {
          setIsSubscribedToPushNotifications(false)
        }

        const disableGpsCheck = process.env.DISABLE_GPS_CHECK === 'true'
        if (disableGpsCheck) {
          setGeolocationBypassed(true)
          setDistance(null)
          setIsLoading(false)
          dispatch(setTransportationForm({ ...transportationForm, distance: 1000 }))
          return
        }

        const currentGeolocation = await getGeolocation()
        const calculatedDistance = Math.ceil(distanceBetweenCoordsInMeters(
          currentGeolocation.latitude,
          currentGeolocation.longitude,
          kioskConfiguration.kioskConfiguration.warehouse.position.lat,
          kioskConfiguration.kioskConfiguration.warehouse.position.lon,
        ) / 1000)

        setDistance(calculatedDistance)
        setIsLoading(false)
        dispatch(setTransportationForm({ ...transportationForm, distance: calculatedDistance }))
      } catch (e) {
        console.log(e)
        setHasLocationError(true)
        setIsLoading(false)
      }
    })()
  }, []);

  const handlePushNotificationToggleButton = async (checked: boolean) => {
    if (checked) {
      if (isPushNotificationSupported() && await askPermissionForPushNotification()) {
        await subscribeUserToPushNotification()
      }
    } else {
      await unsubscribeUserFromPushNotification()
    }
    setIsSubscribedToPushNotifications(await getPushNotificationSubscription() !== null)
  }

  const isInWarehouse = (): boolean => {
    if (geolocationBypassed) return true
    return distance !== null && distance <= kioskConfiguration.kioskConfiguration.warehouse.maxDistance
  }

  let continueBtnText = undefined
  let errorText = undefined
  let showLoaderInContinueBtn = true
  let disabledContinueBtn = true
  let showContinueBtn = true
  if (!isLoading) {
    showLoaderInContinueBtn = false
    if (hasLocationError) {
      continueBtnText = props.page.data.infoBtnText
      errorText = props.page.data.locationErrorText
      disabledContinueBtn = false
    } else if (!isInWarehouse()) {
      errorText = props.page.data.outOfWarehouseText
      disabledContinueBtn = true
      showContinueBtn = false
    } else {
      disabledContinueBtn = false
    }
  }

  return <TextListTemplate page={{
    ...props.page,
    data: {
      ...props.page.data,
      continueBtnText,
      errorText,
      showLoaderInContinueBtn,
      disabledContinueBtn,
      hideContinueBtn: !showContinueBtn,
      onContinue: showContinueBtn ? () => {
        if (hasLocationError) {
          navigate(props.page.data.infoTransitionUrl)
        } else {
          navigate(props.page.data.transitionUrl)
        }
      } : undefined,
      textVariables: new Map([
        ['{currentKm}', geolocationBypassed ? '-' : (distance ?? '-').toString()],
        ['{maxKm}', (kioskConfiguration.kioskConfiguration.warehouse.maxDistance ?? '-').toString()],
      ]),
    },
  }}>
    <div className="flex items-center mb-10">
      {isPushNotificationSupported() && (
        <>
          <span className="mr-4">
            <ToggleButton checked={isSubscribedToPushNotifications} onChange={handlePushNotificationToggleButton} />
          </span>
          <span>
            {getTranslationByPath(language.translations, props.page.data.toggleNotificationsText)}
          </span>
        </>
      )}
      {!isPushNotificationSupported() && isIos() && (
        <>
          <span className="mr-4" dangerouslySetInnerHTML={{ __html: getTranslationByPath(language.translations, props.page.data.iosNotificationText) }}></span>
        </>
      )}
    </div>
  </TextListTemplate>
}
